import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import axios from '../../utils/axiosConfig';
import { setAuthToken } from '../../utils/auth';
import '../../styles/CustomerProfile.css';

function CustomerProfile({ customerId, onClose }) {
  const [customerData, setCustomerData] = useState(null);
  const [newVoucher, setNewVoucher] = useState({ description: '', value: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeemingProgram, setRedeemingProgram] = useState(null);
  const [redeemingVoucher, setRedeemingVoucher] = useState(null);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState(null);
  const [voucherSuccess, setVoucherSuccess] = useState(null);
  const [showRedeemed, setShowRedeemed] = useState(false);
  const [historyTab, setHistoryTab] = useState('vouchers');

  
  // Fetch customer profile
//   const fetchCustomerProfile = async () => {
//     try {
//       setIsLoading(true);
//       const response = await axios.get(`/api/vendors/customer-profile/${customerId}`);
//       setCustomerData(response.data);
//       setError(null);
//     } catch (error) {
//       console.error('Error fetching customer profile:', error);
//       setError('Error loading customer profile');
//     } finally {
//       setIsLoading(false);
//     }
//   };


// Fetch customer profile
const fetchCustomerProfile = async () => {
    try {
      // Don't set loading state if we're already loaded and just updating
      const isInitialLoad = !customerData;
      if (isInitialLoad) {
        setIsLoading(true);
      }
      
      const response = await axios.get(`/api/vendors/customer-profile/${customerId}`);

      if (!response.data.checkins) {
        response.data.checkins = [];
      }
      
      // If we already have data, maintain the program order
      if (customerData && customerData.enrollments && response.data.enrollments) {
        // Create a map of program IDs to their current positions
        const currentOrder = {};
        customerData.enrollments.forEach((program, index) => {
          currentOrder[program.program_id] = index;
        });
        
        // Sort the new enrollments based on the previous order
        response.data.enrollments.sort((a, b) => {
          // If both programs were in the previous order, maintain that order
          if (currentOrder[a.program_id] !== undefined && currentOrder[b.program_id] !== undefined) {
            return currentOrder[a.program_id] - currentOrder[b.program_id];
          }
          // If only a was in the previous order, put it first
          if (currentOrder[a.program_id] !== undefined) {
            return -1;
          }
          // If only b was in the previous order, put it first
          if (currentOrder[b.program_id] !== undefined) {
            return 1;
          }
          // Otherwise, keep the order from the API
          return 0;
        });
      }
      
      setCustomerData(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching customer profile:', error);
      setError('Error loading customer profile');
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetchCustomerProfile = useCallback(
    debounce(() => {
      const token = localStorage.getItem('token');
      if (token) {
        setAuthToken(token);
      }
      fetchCustomerProfile();
    }, 300),
    [customerId]
  );

  useEffect(() => {
    debouncedFetchCustomerProfile();
    return () => {
      debouncedFetchCustomerProfile.cancel();
    };
  }, [debouncedFetchCustomerProfile]);

 
// Redeem stamp
const handleRedeemStamp = async (programId) => {
    try {
      // Save current scroll position
      const scrollPosition = window.scrollY;
      
      setRedeemingProgram(programId);
      setIsRedeeming(true);
      setRedeemSuccess(null);
      setRedeemingVoucher(null); // Clear any voucher redemption state
      
      const response = await axios.post('/api/vendors/redeem-stamp', { customerId, programId });
      
      setRedeemSuccess({
        type: 'success',
        message: 'Stamp redeemed successfully'
      });
      
      // Fetch the updated data without visual refresh
      await fetchCustomerProfile();
      
      // Restore scroll position after data is updated
      window.scrollTo(0, scrollPosition);
    } catch (error) {
      console.error('Error redeeming stamp:', error);
      setRedeemSuccess({
        type: 'error',
        message: error.response?.data?.error || 'Error redeeming stamp'
      });
    } finally {
      setIsRedeeming(false);
      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setRedeemSuccess(null);
      }, 3000);
    }
  };
  
  // Redeem voucher
  const handleRedeemVoucher = async (voucherId) => {
    try {
      // Save current scroll position
      const scrollPosition = window.scrollY;
      
      setRedeemingVoucher(voucherId);
      setIsRedeeming(true);
      setRedeemSuccess(null);
      setRedeemingProgram(null); // Clear any stamp redemption state
      
      const response = await axios.post('/api/vendors/redeem-voucher', { customerId, voucherId });
      
      setRedeemSuccess({
        type: 'success',
        message: response.data.message || 'Voucher redeemed successfully'
      });
      
      // Set showRedeemed to true to expand the redeemed vouchers section
      setShowRedeemed(true);
      
      // Fetch the updated data without visual refresh
      await fetchCustomerProfile();
      
      // Restore scroll position after data is updated
      window.scrollTo(0, scrollPosition);
    } catch (error) {
      console.error('Error redeeming voucher:', error);
      setRedeemSuccess({
        type: 'error',
        message: error.response?.data?.error || 'Error redeeming voucher'
      });
    } finally {
      setIsRedeeming(false);
      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setRedeemSuccess(null);
      }, 3000);
    }
  };

// Generate new promo voucher
const handleGenerateVoucher = async (e) => {
    e.preventDefault();
    
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    if (!newVoucher.description && !newVoucher.value) {
      setVoucherSuccess({
        type: 'error',
        message: 'Please provide either a description or a value for the voucher'
      });
      return;
    }
  
    try {
      setIsRedeeming(true);
      setVoucherSuccess(null);
      
      await axios.post('/api/vendors/generate-voucher', { 
        customerId, 
        description: newVoucher.description || null, 
        value: newVoucher.value || null
      });
      
      // Reset form values based on active tab 
      if (newVoucher.value) {
        setNewVoucher({ description: '', value: '50' });
      } else {
        setNewVoucher({ description: 'Special Offer', value: '' });
      }
      
      setVoucherSuccess({
        type: 'success',
        message: 'Promotional voucher generated successfully'
      });
      
      // Fetch the updated data without visual refresh
      await fetchCustomerProfile();
      
      // Restore scroll position after data is updated
      window.scrollTo(0, scrollPosition);
    } catch (error) {
      console.error('Error generating voucher:', error);
      setVoucherSuccess({
        type: 'error',
        message: error.response?.data?.error || 'Error generating promotional voucher'
      });
    } finally {
      setIsRedeeming(false);
      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setVoucherSuccess(null);
      }, 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="cr-loading">
        <svg className="cr-spinner-large" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
        </svg>
        <span>Loading customer data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cr-error-container">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>{error}</p>
        <button className="cr-button secondary" onClick={fetchCustomerProfile}>
          Try Again
        </button>
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="cr-error-container">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>No customer data available</p>
        <button className="cr-button secondary" onClick={onClose}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="cr-profile-container">
      {/* Customer Information Section */}
      <div className="cr-profile-section">
        <div className="cr-profile-header">
          <div className="cr-profile-avatar">
            {customerData.full_name.charAt(0).toUpperCase()}
          </div>
          <div className="cr-profile-basic-info">
            <h3 className="cr-profile-name">{customerData.full_name}</h3>
            <div className="cr-profile-details">
              {customerData.phone && (
                <div className="cr-profile-detail">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <span>{customerData.phone}</span>
                </div>
              )}
              {customerData.loyalty_number && (
                <div className="cr-profile-detail">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                    <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                    <path d="M18 12a2 2 0 0 0 0 4h4v-4z"></path>
                  </svg>
                  <span>#{customerData.loyalty_number}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Redeemer toast */}
      {/* {redeemSuccess && (
        <div className={`cr-toast ${redeemSuccess.type}`}>
          <div className="cr-toast-content">
            {redeemSuccess.type === 'success' ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            )}
            <span>{redeemSuccess.message}</span>
          </div>
        </div>
      )} */}

      {/* Enrolled Programs Section */}
    <div className="cr-section-title">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
        <path d="M18 12a2 2 0 0 0 0 4h4v-4z"></path>
    </svg>
    <h3>Enrolled Programs</h3>
    </div>

    {/* Stamp Redemption toast - positioned just below section title */}
    {redeemSuccess && redeemingProgram && (
    <div className={`cr-toast ${redeemSuccess.type}`}>
        <div className="cr-toast-content">
        {redeemSuccess.type === 'success' ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
        ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
        )}
        <span>{redeemSuccess.message}</span>
        </div>
    </div>
    )}

      {customerData.enrollments && customerData.enrollments.length > 0 ? (
        <div className="cr-programs-grid">
          {customerData.enrollments.map(enrollment => (
            <div key={enrollment.program_id} className="cr-program-card">
              <div className="cr-program-header">
                <h4>{enrollment.program_name}</h4>
                <div className="cr-program-progress">
                  <div className="cr-progress-text">
                    <span className="cr-progress-current">{enrollment.stamps}</span>
                    <span className="cr-progress-slash">/</span>
                    <span className="cr-progress-total">{enrollment.stamps_required}</span>
                  </div>
                  <div className="cr-progress-bar">
                    <div 
                      className="cr-progress-fill" 
                      style={{ width: `${(enrollment.stamps / enrollment.stamps_required) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="cr-program-stamps">
                {Array.from({ length: enrollment.stamps_required }).map((_, i) => (
                  <div 
                    key={i} 
                    className={`cr-stamp ${i < enrollment.stamps ? 'cr-stamp-filled' : ''}`}
                  ></div>
                ))}
              </div>
              <div className="cr-program-footer">
                <button 
                  className="cr-button primary"
                  onClick={() => handleRedeemStamp(enrollment.program_id)}
                  disabled={isRedeeming && redeemingProgram === enrollment.program_id}
                >
                  {isRedeeming && redeemingProgram === enrollment.program_id ? (
                    <>
                      <svg className="cr-spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                      </svg>
                      <span>Redeeming...</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                      <span>Redeem Stamp</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="cr-empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
            <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
            <path d="M18 12a2 2 0 0 0 0 4h4v-4z"></path>
          </svg>
          <p>Customer is not enrolled in any loyalty programs</p>
        </div>
      )}

      {/* Vouchers Section */}
        <div className="cr-section-title">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            <line x1="9" y1="9" x2="9" y2="9"></line>
            <line x1="9" y1="13" x2="9" y2="13"></line>
            <line x1="13" y1="9" x2="13" y2="9"></line>
            <line x1="13" y1="13" x2="13" y2="13"></line>
        </svg>
        <h3>Available Vouchers</h3>
        </div>

        {/* Voucher Redemption toast - positioned just below section title */}
        {redeemSuccess && redeemingVoucher && (
        <div className={`cr-toast ${redeemSuccess.type}`}>
            <div className="cr-toast-content">
            {redeemSuccess.type === 'success' ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            )}
            <span>{redeemSuccess.message}</span>
            </div>
        </div>
        )}

      {customerData.vouchers && customerData.vouchers.filter(v => !v.redeemed).length > 0 ? (
        <div className="cr-vouchers-container">
          {customerData.vouchers
            .filter(voucher => !voucher.redeemed)
            .map(voucher => (
              <div key={voucher.id} className="cr-voucher-card">
                <div className="cr-voucher-content">
                  <div className="cr-voucher-info">
                    <h4>
                      {voucher.program_name !== 'Standalone' 
                        ? `${voucher.program_name}: ${voucher.description || 'No description'}`
                        : (voucher.description || `Value: R${voucher.value || 0}`)
                      }
                    </h4>
                    <div className="cr-voucher-date">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span>Created: {new Date(voucher.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <button 
                    className="cr-button secondary"
                    onClick={() => handleRedeemVoucher(voucher.id)}
                    disabled={isRedeeming && redeemingVoucher === voucher.id}
                  >
                    {isRedeeming && redeemingVoucher === voucher.id ? (
                      <>
                        <svg className="cr-spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                        </svg>
                        <span>Redeeming...</span>
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>Redeem Voucher</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="cr-empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            <line x1="9" y1="9" x2="9" y2="9"></line>
            <line x1="9" y1="13" x2="9" y2="13"></line>
            <line x1="13" y1="9" x2="13" y2="9"></line>
            <line x1="13" y1="13" x2="13" y2="13"></line>
          </svg>
          <p>No available vouchers found</p>
        </div>
      )}

      {/* Redeemed Vouchers Section (Collapsible) */}
      {customerData.vouchers && customerData.vouchers.filter(v => v.redeemed).length > 0 && (
        <div className="cr-collapsible-section">
          <button className="cr-collapsible-trigger" onClick={() => setShowRedeemed(!showRedeemed)}>
            <div className="cr-section-title">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                <line x1="9" y1="9" x2="9" y2="9"></line>
                <line x1="9" y1="13" x2="9" y2="13"></line>
                <line x1="13" y1="9" x2="13" y2="9"></line>
                <line x1="13" y1="13" x2="13" y2="13"></line>
              </svg>
              <h3>Redeemed Vouchers</h3>
            </div>
            <svg className={`cr-arrow ${showRedeemed ? 'open' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          
          {showRedeemed && (
            <div className="cr-vouchers-container redeemed">
              {customerData.vouchers
                .filter(voucher => voucher.redeemed)
                .map(voucher => (
                  <div key={voucher.id} className="cr-voucher-card redeemed">
                    <div className="cr-voucher-content">
                      <div className="cr-voucher-info">
                        <h4>
                          {voucher.program_name !== 'Standalone' 
                            ? `${voucher.program_name}: ${voucher.description || 'No description'}`
                            : (voucher.description || `Value: R${voucher.value || 0}`)
                          }
                        </h4>
                        <div className="cr-voucher-dates">
                          <div className="cr-voucher-date">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                            <span>Created: {new Date(voucher.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="cr-voucher-date">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Redeemed: {new Date(voucher.redeemed_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="cr-redeemed-label">Redeemed</div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

   
      {/* Create Promo Voucher Form */}
        <div className="cr-section-title">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 14.5l-2-2V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12.5l5 5"></path>
            <path d="M15 6h.01"></path>
            <path d="M18 6h.01"></path>
            <path d="M15 9h.01"></path>
            <path d="M18 9h.01"></path>
            <path d="M15 12h.01"></path>
            <path d="M18 12h.01"></path>
            <path d="M15 15h.01"></path>
        </svg>
        <h3>Generate Promotional Voucher</h3>
        </div>

        <div className="cr-info-box">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <span>Promotional vouchers are special rewards you can give to valued customers outside of the regular loyalty program. These will appear in their loyalty profile.</span>
        </div>
      
      {voucherSuccess && (
        <div className={`cr-toast ${voucherSuccess.type}`}>
          <div className="cr-toast-content">
            {voucherSuccess.type === 'success' ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            )}
            <span>{voucherSuccess.message}</span>
          </div>
        </div>
      )}
      
      <div className="cr-voucher-type-selector">
            <div className="cr-tabs">
                <button 
                type="button"
                className={`cr-tab ${!newVoucher.value ? 'active' : ''}`}
                onClick={() => setNewVoucher({ description: newVoucher.description || 'Special Offer', value: '' })}
                >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                Description-based
                </button>
                <button 
                type="button"
                className={`cr-tab ${newVoucher.value ? 'active' : ''}`}
                onClick={() => setNewVoucher({ description: '', value: newVoucher.value || '50' })}
                >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Value-based
                </button>
            </div>
            </div>

            <form className="cr-voucher-form" onSubmit={handleGenerateVoucher}>
            {!newVoucher.value ? (
                <div className="cr-form-group full-width">
                <label htmlFor="voucherDescription">Promo Description</label>
                <input
                    id="voucherDescription"
                    type="text"
                    className="cr-form-control"
                    placeholder="e.g., Free glass of wine, Complimentary dessert"
                    value={newVoucher.description}
                    onChange={(e) => setNewVoucher({...newVoucher, description: e.target.value})}
                />
                <small className="cr-form-text">Describe what this promotional voucher offers the customer</small>
                </div>
            ) : (
                <div className="cr-form-group full-width">
                <label htmlFor="voucherValue">Value in Rand</label>
                <div className="cr-input-group">
                    <span className="cr-input-prefix">R</span>
                    <input
                    id="voucherValue"
                    type="number"
                    className="cr-form-control"
                    placeholder="e.g., 50"
                    value={newVoucher.value}
                    onChange={(e) => setNewVoucher({...newVoucher, value: e.target.value})}
                    />
                </div>
                <small className="cr-form-text">Specify the monetary value of this promotional voucher</small>
                </div>
            )}
            
            <button 
            type="submit" 
            className="cr-promo-voucher-button"
            disabled={isRedeeming || (!newVoucher.description && !newVoucher.value)}
            >
            {isRedeeming ? (
                <>
                <svg className="cr-spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                </svg>
                <span>Generating...</span>
                </>
            ) : (
                <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="12" rx="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="16" x2="12" y2="21"></line>
                </svg>
                <span>Generate Promo Voucher</span>
                </>
            )}
            </button>
            </form>

{/* Customer History Section */}
<div className="cr-section-title">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
  </svg>
  <h3>Customer History</h3>
</div>

<div className="cr-history-container">
  {/* Tabs for history types */}
  <div className="cr-history-tabs">
    <button 
      className={historyTab === 'vouchers' ? 'cr-tab active' : 'cr-tab'} 
      onClick={() => setHistoryTab('vouchers')}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
      Voucher History
    </button>
    <button 
      className={historyTab === 'checkins' ? 'cr-tab active' : 'cr-tab'} 
      onClick={() => setHistoryTab('checkins')}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M12 6v6l4 2"></path>
      </svg>
      Visit History
    </button>
  </div>

  {/* Vouchers History */}
  {historyTab === 'vouchers' && (
    <div className="cr-history-content">
          {customerData.vouchers && customerData.vouchers.filter(v => v.redeemed_at || v.redeemed).length > 0 ? (
      <div className="cr-vouchers-list">
        {customerData.vouchers
          .filter(v => v.redeemed_at || v.redeemed)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .map(voucher => (
              <div key={voucher.id} className="cr-history-item">
                <div className="cr-history-icon">
                  {voucher.redeemed_at || voucher.redeemed ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  )}
                </div>
                <div className="cr-history-details">
                  <h4>
                    {voucher.program_name !== 'Standalone' 
                      ? `${voucher.program_name}: ${voucher.description || 'No description'}`
                      : (voucher.description || `Value: R${voucher.value || 0}`)
                    }
                  </h4>
                  <div className="cr-history-dates">
                    <span>Created: {new Date(voucher.created_at).toLocaleDateString()}</span>
                    {(voucher.redeemed_at || voucher.redeemed) && (
                      <span>Redeemed: {voucher.redeemed_at ? new Date(voucher.redeemed_at).toLocaleDateString() + 
                        (voucher.redeemed_at ? ' at ' + 
                        new Date(voucher.redeemed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '') : 'Redeemed'}</span>
                    )}
                  </div>
                </div>
                <div className={`cr-voucher-status ${(voucher.redeemed_at || voucher.redeemed) ? 'redeemed' : 'available'}`}>
                  {(voucher.redeemed_at || voucher.redeemed) ? 'Redeemed' : 'Available'}
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="cr-empty-history">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <p>No voucher history found for this customer</p>
        </div>
      )}
    </div>
  )}

  {/* Visit/Checkin History */}
  {historyTab === 'checkins' && (
    <div className="cr-history-content">
      {customerData.visit_history && customerData.visit_history.length > 0 ? (
        <div className="cr-checkins-list">
          {customerData.visit_history.map((visit, index) => (
            <div key={`visit-${index}`} className="cr-history-item">
              <div className="cr-history-icon">
                {visit.type === 'redemption' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                )}
              </div>
              <div className="cr-history-details">
                <h4>{visit.program_name}</h4>
                <div className="cr-visit-date">
                  {new Date(visit.date).toLocaleDateString()}
                  {visit.time && ` at ${visit.time}`}
                </div>
                {visit.stamps_earned && (
                  <div className="cr-stamps-earned">
                    Stamps earned: {visit.stamps_earned}
                  </div>
                )}
              </div>
              <div className={`cr-visit-type ${visit.type === 'redemption' ? 'redemption' : 'checkin'}`}>
                {visit.type === 'redemption' ? 'Redemption' : 'Visit'}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="cr-empty-history">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 6v6l4 2"></path>
          </svg>
          <p>No visit history found for this customer</p>
          <small>Visit history includes check-ins and voucher redemptions</small>
        </div>
      )}
    </div>
  )}
</div>

    </div>
  );
}

export default CustomerProfile;